import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Input, Row, Typography, notification } from "antd";
import { updateAuthorizationHeader } from "../../api/axiosClient";
import {
  requestOtp,
  verifyOtp,
  requestPortalSsoJwt,
  exchangeSsoJwt,
  experienceErrorMessage,
} from "../../api/experienceAuth";
import { useAppDispatch } from "../../redux/hooks";
import { getMe } from "../../redux/apiActions/authActions";

const { Text, Link } = Typography;

const RESEND_SECONDS = 30;

/**
 * Two-step login against RedTeam Xperience: email -> 6-digit OTP, the same flow
 * the Xperience student portal uses. Xperience is the identity source; a
 * candidate who exists there is created in the exam portal automatically when
 * the SSO JWT is exchanged. See `api/experienceAuth.ts` for the full chain.
 */
const Login: React.FC = () => {
  const [step, setStep] = useState<"email" | "otp">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dispatch = useAppDispatch();

  const startResendTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSecondsLeft(RESEND_SECONDS);
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    []
  );

  const fail = (error: any, fallback: string) =>
    notification.error({
      message: "Login failed",
      description: experienceErrorMessage(error, fallback),
    });

  // Step 1 — confirm the account exists in Xperience and email the code.
  const sendOtp = async (address: string) => {
    setLoading(true);
    try {
      const message = await requestOtp(address);
      setEmail(address);
      setOtp("");
      setStep("otp");
      startResendTimer();
      notification.success({ message: "Code sent", description: message });
    } catch (error: any) {
      fail(error, "Could not send the code. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const onSubmitEmail = () => {
    const address = email.trim().toLowerCase();
    if (address) sendOtp(address);
  };

  // Step 2 — verify the code, then SSO into the portal (which creates the user
  // there on first login) and load the session.
  const onVerify = async () => {
    const code = otp.trim();
    if (!code) return;

    setLoading(true);
    try {
      const { token } = await verifyOtp(email, code);
      const ssoJwt = await requestPortalSsoJwt(token);
      const portalToken = await exchangeSsoJwt(ssoJwt);

      updateAuthorizationHeader(portalToken);
      // getMe sets isAuthenticated + homeRoute together, so the /login route
      // guard redirects to the role's home on its own — no navigate needed.
      await dispatch(getMe()).unwrap();
    } catch (error: any) {
      fail(error, "Invalid or expired code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login_container">
      <div className="login_section">
        <Row justify="center">
          <img src={require("../../assets/logo.png")} alt="logo" />
        </Row>
        <h1 style={{ textAlign: "center" }}>Login</h1>

        {step === "email" ? (
          <Form className="login_form" onFinish={onSubmitEmail} layout="vertical">
            <Row justify="center" style={{ marginBottom: "1.5rem" }}>
              <Text style={{ color: "#f4f4f4" }}>
                Enter your email and we&rsquo;ll send you a one-time code.
              </Text>
            </Row>
            <Form.Item>
              <Input
                className="login-input"
                type="email"
                placeholder="you@example.com"
                autoFocus
                autoComplete="username"
                value={email}
                disabled={loading}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="login_btn"
              loading={loading}
              disabled={!email.trim()}
            >
              Send code
            </Button>
          </Form>
        ) : (
          <Form className="login_form" onFinish={onVerify} layout="vertical">
            <Row justify="center" style={{ marginBottom: "1.5rem" }}>
              <Text style={{ color: "#f4f4f4", textAlign: "center" }}>
                We sent a 6-digit verification code to <b>{email}</b>.
              </Text>
            </Row>
            <Form.Item>
              <Input
                className="login-input"
                placeholder="000000"
                autoFocus
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                disabled={loading}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              className="login_btn"
              loading={loading}
              disabled={!otp.trim()}
            >
              Verify &amp; continue
            </Button>
            <Row justify="space-between" style={{ marginTop: "1.5rem" }}>
              <Link
                onClick={() => {
                  if (!loading) setStep("email");
                }}
              >
                Change email
              </Link>
              {secondsLeft > 0 ? (
                <Text style={{ color: "#bbc1cd" }}>
                  Resend in 0:{secondsLeft < 10 ? "0" : ""}
                  {secondsLeft}
                </Text>
              ) : (
                <Link
                  onClick={() => {
                    if (!loading) sendOtp(email);
                  }}
                >
                  Resend code
                </Link>
              )}
            </Row>
          </Form>
        )}
      </div>
    </div>
  );
};

export default Login;
